import React, { useState, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useSearchParams, useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { ThemeSelect } from '@/components/ui/theme-toggle';
import { toast } from 'sonner';
import { Loader2, AlertCircle, Palette, Edit3, X } from 'lucide-react';
import axiosInstance from '@/lib/enhancedAxiosInstance';
import { OrganizationSettingsForm } from '@/features/settings/components/settings/OrganizationSettingsForm';
import { SubscriptionManagement } from '@/components/settings/SubscriptionManagement';
import { FamilyListPage } from '@/features/families/FamilyListPage';
import AttributesPage from '@/pages/AttributesPage';
import AttributeGroupsPage from '@/pages/AttributeGroupsPage';
import { ApiManagement } from '@/features/settings/components/ApiManagement';
import { config } from '@/config/config';
import { useSettingspageTranslations } from '@/hooks/useSettingspageTranslations';
import { logError } from '@/utils/errorHandler';

// Define which tabs require admin permissions
const adminOnlyTabs = ['organization', 'attributes', 'attribute-groups', 'families'];

// --- Settings Page Component ---
const SettingsPage: React.FC = () => {
  // Get translations
  const i18n = useSettingspageTranslations();

  // --- Zod Schemas ---
  const profileSchema = z.object({
    first_name: z.string().min(1, i18n.validation.firstNameRequired),
    last_name: z.string().min(1, i18n.validation.lastNameRequired),
  });
  type ProfileFormData = z.infer<typeof profileSchema>;

  const passwordSchema = z
    .object({
      currentPassword: z.string().min(1, i18n.validation.currentPasswordRequired),
      newPassword: z.string().min(6, i18n.validation.newPasswordMinLength),
      confirmPassword: z.string().min(1, i18n.validation.confirmPasswordRequired),
    })
    .refine(data => data.newPassword === data.confirmPassword, {
      message: i18n.validation.passwordsDontMatch,
      path: ['confirmPassword'], // path of error
    });
  type PasswordFormData = z.infer<typeof passwordSchema>;

  const { user, updateUserContext, checkPermission, loading: authLoading } = useAuth();

  // Check permissions at the top level
  const lowerRole = user?.role?.toString().toLowerCase();
  const isOrgAdmin = lowerRole === 'admin' || lowerRole === 'owner' || lowerRole === 'orgowner';
  const hasSettingsViewPermission = checkPermission('settings.view');
  const hasSettingsChangePermission = checkPermission('settings.change');
  const hasSettingsPermission = hasSettingsViewPermission || hasSettingsChangePermission;

  // Check API access using the same logic as ApiManagement component
  const hasApiAccess = (() => {
    // Allow Django superusers (KernLogic staff)
    if (user?.is_superuser) return true;

    // Organization owners and admins have API access
    if (isOrgAdmin) return true;

    // Check for existing backend permissions
    if (checkPermission('settings.change')) return true;
    if (checkPermission('settings.view')) return true;

    return false;
  })();

  // Filter tabs based on user permissions using useMemo for performance
  const availableTabs = useMemo(() => {
    const allTabs = config.settings.display.tabs;

    // If user is staff (system admin), show all tabs
    if (user?.is_staff) {
      return allTabs;
    }

    return allTabs.filter(tab => {
      // Always show profile, security, and preferences to everyone
      if (['profile', 'security', 'preferences'].includes(tab.value)) {
        return true;
      }

      // API tab requires the same permissions as API content
      if (tab.value === 'api') {
        return hasApiAccess;
      }

      // Admin-only tabs require admin role or settings permissions
      if (adminOnlyTabs.includes(tab.value)) {
        return isOrgAdmin || hasSettingsPermission;
      }

      return true;
    });
  }, [user?.is_staff, isOrgAdmin, hasSettingsPermission, hasApiAccess]);

  // Get the tab from the URL query parameters
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const tabParam = searchParams.get('tab');

  // Validate the tab parameter is one of the available tabs
  const validTabs = availableTabs.map(tab => tab.value);
  const currentTab = validTabs.includes(tabParam || '') ? tabParam || validTabs[0] : validTabs[0];

  const { theme, setTheme } = useTheme();
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isSavingPassword, setIsSavingPassword] = useState(false);
  const [isEditingProfile, setIsEditingProfile] = useState(false);

  // Theme preferences state
  const [selectedTheme, setSelectedTheme] = useState(theme);
  const [isSavingPreferences, setIsSavingPreferences] = useState(false);

  // --- React Hook Form Instances ---
  const {
    register: registerProfile,
    handleSubmit: handleProfileSubmitHook,
    formState: { errors: profileErrors },
    reset: resetProfileForm,
  } = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      first_name: user?.first_name || '',
      last_name: user?.last_name || '',
    },
  });

  const {
    register: registerPassword,
    handleSubmit: handlePasswordSubmitHook,
    formState: { errors: passwordErrors },
    reset: resetPasswordForm,
    setError: setPasswordError,
  } = useForm<PasswordFormData>({
    resolver: zodResolver(passwordSchema),
  });

  // Update selected theme when context theme changes
  useEffect(() => {
    setSelectedTheme(theme);
  }, [theme]);

  // --- Update Default Values on User Change ---
  useEffect(() => {
    if (user) {
      resetProfileForm({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
      });
    }
  }, [user, resetProfileForm]);

  // Show loading state while auth is initializing to prevent permission flicker
  if (authLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <div className="mx-auto mb-4 size-8 animate-spin rounded-full border-b-2 border-primary"></div>
          <p className="text-muted-foreground">
            {i18n.loadingStates?.loadingPermissions || 'Loading...'}
          </p>
        </div>
      </div>
    );
  }

  // Handle tab changes by updating the URL
  const handleTabChange = (tabValue: string) => {
    navigate(`/app/settings?tab=${tabValue}`, { replace: true });
  };

  // --- Handlers ---
  const handleProfileUpdate = async (data: ProfileFormData) => {
    setIsSavingProfile(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await axiosInstance.put('/api/auth/user/', data, {
        headers: { Authorization: `Bearer ${token}` },
      });
      updateUserContext(response.data); // Update context state
      setIsEditingProfile(false); // Exit edit mode
      toast.success(i18n.profile.successMessage);
    } catch (err: any) {
      logError(
        'Failed to update user profile',
        {
          component: 'SettingsPage',
          action: 'updateProfile',
          metadata: {
            userId: user?.id,
            error: err,
          },
        },
        {
          showToast: true,
          toastMessage: err.response?.data?.detail || err.message || 'Failed to update profile',
          userFacing: true,
        }
      );
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditingProfile(false);
    // Reset form to current user data
    resetProfileForm({
      first_name: user?.first_name || '',
      last_name: user?.last_name || '',
    });
  };

  const handlePasswordChange = async (data: PasswordFormData) => {
    setIsSavingPassword(true);
    try {
      const token = localStorage.getItem('access_token');
      await axiosInstance.post(
        '/api/auth/password/change/',
        {
          old_password: data.currentPassword,
          new_password1: data.newPassword,
          new_password2: data.confirmPassword,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      toast.success(i18n.security.successMessage);
      resetPasswordForm(); // Clear fields on success
    } catch (err: any) {
      logError(
        'Failed to change user password',
        {
          component: 'SettingsPage',
          action: 'changePassword',
          metadata: {
            userId: user?.id,
            error: err.response?.data || err,
          },
        },
        {
          showToast: false, // We handle specific field errors below
          userFacing: true,
        }
      );
      // Map potential backend errors to form fields
      if (err.response?.data?.old_password) {
        setPasswordError('currentPassword', {
          type: 'server',
          message: err.response.data.old_password[0],
        });
      } else if (err.response?.data?.new_password2) {
        setPasswordError('newPassword', {
          type: 'server',
          message: err.response.data.new_password2[0],
        });
        setPasswordError('confirmPassword', {
          type: 'server',
          message: err.response.data.new_password2[0],
        });
      } else {
        const errorMsg = err.response?.data?.detail || err.message || i18n.errors.passwordChange;
        toast.error(errorMsg);
      }
    } finally {
      setIsSavingPassword(false);
    }
  };

  // Handle saving preferences
  const handleSavePreferences = async () => {
    setIsSavingPreferences(true);
    try {
      // Update the theme in context
      setTheme(selectedTheme);

      // Here you could also save to backend if needed
      // await axiosInstance.post('/api/user/preferences/', { theme: selectedTheme });

      toast.success(i18n.preferences.successMessage);
    } catch (err: any) {
      logError(
        'Failed to save user preferences',
        {
          component: 'SettingsPage',
          action: 'savePreferences',
          metadata: {
            userId: user?.id,
            selectedTheme,
            error: err,
          },
        },
        {
          showToast: true,
          toastMessage: 'Failed to save preferences',
          userFacing: true,
        }
      );
    } finally {
      setIsSavingPreferences(false);
    }
  };

  // --- Render ---
  return (
    <div className="flex h-full flex-col">
      <div className="mx-auto w-full max-w-none px-4 py-8">
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold tracking-tight text-foreground">
                {i18n.mainPage.pageTitle}
              </h1>
              <p className="mt-2 max-w-2xl text-lg text-muted-foreground">
                {i18n.mainPage.pageDescription}
              </p>
            </div>
          </div>
        </div>

        <Tabs value={currentTab} onValueChange={handleTabChange} className="flex h-full flex-col">
          {/* Enhanced Tab Navigation with Sticky Positioning */}
          <div className="sticky top-0 z-10 mb-6 border-b border-border bg-background/95 pb-6 backdrop-blur-sm">
            <div className="enterprise-tabs w-full rounded-xl border border-border bg-card p-1.5 shadow-sm">
              <TabsList className="enterprise-tabs-list flex w-full min-w-0 flex-wrap gap-1 rounded-lg bg-muted/30 p-1">
                {availableTabs.map(tab => (
                  <TabsTrigger
                    key={tab.value}
                    value={tab.value}
                    className={`
                      enterprise-tab-trigger relative min-w-0 flex-1 rounded-md border-0 px-3
                      py-2.5 text-sm font-medium
                      text-muted-foreground transition-all duration-200
                      hover:bg-muted/50
                      hover:text-foreground focus:outline-none
                      focus:ring-1 focus:ring-primary/20 data-[state=active]:border-0 data-[state=active]:bg-primary
                      data-[state=active]:font-semibold data-[state=active]:text-primary-foreground
                      data-[state=active]:shadow-sm
                    `}
                  >
                    {i18n.tabs[tab.value as keyof typeof i18n.tabs] || tab.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>
          </div>

          {/* Tab Content with Fixed Height and Scrolling */}
          <div className="flex-1 overflow-hidden">
            <div className="h-full overflow-auto rounded-xl border border-border bg-card shadow-sm">
              {/* Profile Tab */}
              <TabsContent value="profile" className="m-0 h-full">
                <div className="p-8">
                  <div className="w-full">
                    <div className="mb-6 flex items-center space-x-3">
                      <div className="flex size-10 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-900/30">
                        <svg
                          className="size-5 text-blue-600 dark:text-blue-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                          />
                        </svg>
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold text-foreground">{i18n.profile.title}</h2>
                        <p className="text-muted-foreground">{i18n.profile.description}</p>
                      </div>
                    </div>

                    {isEditingProfile ? (
                      <form
                        onSubmit={handleProfileSubmitHook(handleProfileUpdate)}
                        className="space-y-6"
                      >
                        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                          <div className="space-y-2">
                            <Label
                              htmlFor="setting-first-name"
                              className="text-sm font-semibold text-foreground"
                            >
                              {i18n.profile.firstNameLabel}
                            </Label>
                            <Input
                              id="setting-first-name"
                              {...registerProfile('first_name')}
                              placeholder={i18n.placeholders.yourFirstName}
                              disabled={isSavingProfile}
                              className={`h-11 ${profileErrors.first_name ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-border focus:border-primary focus:ring-primary'}`}
                            />
                            {profileErrors.first_name && (
                              <p className="mt-1 flex items-center text-sm text-red-600 dark:text-red-400">
                                <AlertCircle className="mr-1 size-4" />
                                {profileErrors.first_name.message}
                              </p>
                            )}
                          </div>
                          <div className="space-y-2">
                            <Label
                              htmlFor="setting-last-name"
                              className="text-sm font-semibold text-foreground"
                            >
                              {i18n.profile.lastNameLabel}
                            </Label>
                            <Input
                              id="setting-last-name"
                              {...registerProfile('last_name')}
                              placeholder={i18n.placeholders.yourLastName}
                              disabled={isSavingProfile}
                              className={`h-11 ${profileErrors.last_name ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-border focus:border-primary focus:ring-primary'}`}
                            />
                            {profileErrors.last_name && (
                              <p className="mt-1 flex items-center text-sm text-red-600 dark:text-red-400">
                                <AlertCircle className="mr-1 size-4" />
                                {profileErrors.last_name.message}
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="flex justify-end space-x-3 border-t border-border pt-4">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={handleCancelEdit}
                            disabled={isSavingProfile}
                          >
                            <X className="mr-2 size-4" />
                            {i18n.profile.cancelButton}
                          </Button>
                          <Button type="submit" disabled={isSavingProfile} variant="primary">
                            {isSavingProfile ? (
                              <>
                                <Loader2 className="mr-2 size-4 animate-spin" />
                                {i18n.profile.savingLabel}
                              </>
                            ) : (
                              i18n.profile.saveButton
                            )}
                          </Button>
                        </div>
                      </form>
                    ) : (
                      <div className="space-y-6">
                        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                          <div className="space-y-2">
                            <Label className="text-sm font-semibold text-foreground">
                              {i18n.profile.firstNameLabel}
                            </Label>
                            <div className="flex h-11 items-center rounded-md border border-border bg-muted/50 px-3">
                              <span className="text-foreground">{user?.first_name || '—'}</span>
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label className="text-sm font-semibold text-foreground">
                              {i18n.profile.lastNameLabel}
                            </Label>
                            <div className="flex h-11 items-center rounded-md border border-border bg-muted/50 px-3">
                              <span className="text-foreground">{user?.last_name || '—'}</span>
                            </div>
                          </div>
                        </div>

                        <div className="flex justify-end border-t border-border pt-4">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setIsEditingProfile(true)}
                          >
                            <Edit3 className="mr-2 size-4" />
                            {i18n.profile.editButton}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </TabsContent>

              {/* Security Tab */}
              <TabsContent value="security" className="m-0 h-full">
                <div className="p-8">
                  <div className="w-full">
                    <div className="mb-6 flex items-center space-x-3">
                      <div className="flex size-10 items-center justify-center rounded-lg bg-emerald-100 dark:bg-emerald-900/30">
                        <svg
                          className="size-5 text-emerald-600 dark:text-emerald-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                          />
                        </svg>
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold text-foreground">
                          {i18n.security.title}
                        </h2>
                        <p className="text-muted-foreground">{i18n.security.description}</p>
                      </div>
                    </div>

                    <form
                      onSubmit={handlePasswordSubmitHook(handlePasswordChange)}
                      className="space-y-6"
                    >
                      {/* Hidden username field for accessibility - password managers expect this */}
                      <input type="hidden" autoComplete="username" value={user?.email || ''} />

                      <div className="space-y-2">
                        <Label
                          htmlFor="setting-current-password"
                          className="text-sm font-semibold text-foreground"
                        >
                          {i18n.security.currentPasswordLabel}
                        </Label>
                        <Input
                          id="setting-current-password"
                          type="password"
                          autoComplete="current-password"
                          {...registerPassword('currentPassword')}
                          placeholder={i18n.placeholders.enterCurrentPassword}
                          disabled={isSavingPassword}
                          className={`h-11 ${passwordErrors.currentPassword ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-border focus:border-primary focus:ring-primary'}`}
                        />
                        {passwordErrors.currentPassword && (
                          <p className="mt-1 flex items-center text-sm text-red-600 dark:text-red-400">
                            <AlertCircle className="mr-1 size-4" />
                            {passwordErrors.currentPassword.message}
                          </p>
                        )}
                      </div>

                      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                        <div className="space-y-2">
                          <Label
                            htmlFor="setting-new-password"
                            className="text-sm font-semibold text-foreground"
                          >
                            {i18n.security.newPasswordLabel}
                          </Label>
                          <Input
                            id="setting-new-password"
                            type="password"
                            autoComplete="new-password"
                            {...registerPassword('newPassword')}
                            placeholder={i18n.placeholders.enterNewPassword}
                            disabled={isSavingPassword}
                            className={`h-11 ${passwordErrors.newPassword ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-border focus:border-primary focus:ring-primary'}`}
                          />
                          {passwordErrors.newPassword && (
                            <p className="mt-1 flex items-center text-sm text-red-600 dark:text-red-400">
                              <AlertCircle className="mr-1 size-4" />
                              {passwordErrors.newPassword.message}
                            </p>
                          )}
                        </div>
                        <div className="space-y-2">
                          <Label
                            htmlFor="setting-confirm-password"
                            className="text-sm font-semibold text-foreground"
                          >
                            {i18n.security.confirmPasswordLabel}
                          </Label>
                          <Input
                            id="setting-confirm-password"
                            type="password"
                            autoComplete="new-password"
                            {...registerPassword('confirmPassword')}
                            placeholder={i18n.placeholders.confirmNewPassword}
                            disabled={isSavingPassword}
                            className={`h-11 ${passwordErrors.confirmPassword ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-border focus:border-primary focus:ring-primary'}`}
                          />
                          {passwordErrors.confirmPassword && (
                            <p className="mt-1 flex items-center text-sm text-red-600 dark:text-red-400">
                              <AlertCircle className="mr-1 size-4" />
                              {passwordErrors.confirmPassword.message}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="flex justify-end border-t border-border pt-4">
                        <Button type="submit" disabled={isSavingPassword} variant="primary">
                          {isSavingPassword ? (
                            <>
                              <Loader2 className="mr-2 size-4 animate-spin" />
                              {i18n.security.savingLabel}
                            </>
                          ) : (
                            i18n.security.updateButton
                          )}
                        </Button>
                      </div>
                    </form>
                  </div>
                </div>
              </TabsContent>

              {/* Preferences Tab */}
              <TabsContent value="preferences" className="m-0 h-full">
                <div className="p-8">
                  <div className="w-full">
                    <div className="mb-6 flex items-center space-x-3">
                      <div className="flex size-10 items-center justify-center rounded-lg bg-purple-100 dark:bg-purple-900/30">
                        <Palette className="size-5 text-purple-600 dark:text-purple-400" />
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold text-foreground">
                          {i18n.preferences.title}
                        </h2>
                        <p className="text-muted-foreground">{i18n.preferences.description}</p>
                      </div>
                    </div>

                    <div className="space-y-6">
                      <div className="space-y-4">
                        <div>
                          <h3 className="text-lg font-semibold text-foreground">
                            {i18n.preferences.themeTitle}
                          </h3>
                          <p className="mt-1 text-sm text-muted-foreground">
                            {i18n.preferences.themeDescription2}
                          </p>
                        </div>

                        <ThemeSelect
                          value={selectedTheme}
                          onValueChange={(value: string) =>
                            setSelectedTheme(value as 'light' | 'dark' | 'system')
                          }
                        />
                      </div>

                      <div className="flex justify-end border-t border-border pt-4">
                        <Button
                          onClick={handleSavePreferences}
                          disabled={isSavingPreferences}
                          variant="primary"
                        >
                          {isSavingPreferences ? (
                            <>
                              <Loader2 className="mr-2 size-4 animate-spin" />
                              {i18n.preferences.savingLabel}
                            </>
                          ) : (
                            i18n.preferences.updateButton
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* Organization Tab */}
              {availableTabs.some(tab => tab.value === 'organization') && (
                <TabsContent value="organization" className="m-0 h-full">
                  <div className="p-8" data-tour-target="organization-settings">
                    <div className="mb-6 flex items-center space-x-3">
                      <div className="flex size-10 items-center justify-center rounded-lg bg-orange-100 dark:bg-orange-900/30">
                        <svg
                          className="size-5 text-orange-600 dark:text-orange-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                          />
                        </svg>
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold text-foreground">
                          {i18n.organization.title}
                        </h2>
                        <p className="text-muted-foreground">{i18n.organization.description}</p>
                      </div>
                    </div>

                    {/* Organization Subtabs */}
                    <Tabs defaultValue="settings" className="w-full">
                      <TabsList className="mb-6 h-auto border-b bg-background p-0">
                        <TabsTrigger
                          value="settings"
                          className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                        >
                          {i18n.organization.tabs?.settings || 'Organization Settings'}
                        </TabsTrigger>
                        <TabsTrigger
                          value="subscription"
                          className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                        >
                          {i18n.organization.tabs?.subscription || 'Manage Subscription'}
                        </TabsTrigger>
                      </TabsList>

                      <TabsContent value="settings" className="m-0">
                        <OrganizationSettingsForm />
                      </TabsContent>

                      <TabsContent value="subscription" className="m-0">
                        <SubscriptionManagement />
                      </TabsContent>
                    </Tabs>
                  </div>
                </TabsContent>
              )}

              {/* API Keys Tab */}
              <TabsContent value="api" className="m-0 h-full">
                <div className="p-8">
                  <div className="w-full">
                    <div className="mb-6 flex items-center space-x-3">
                      <div className="flex size-10 items-center justify-center rounded-lg bg-indigo-100 dark:bg-indigo-900/30">
                        <svg
                          className="size-5 text-indigo-600 dark:text-indigo-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                          />
                        </svg>
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold text-foreground">{i18n.api.title}</h2>
                        <p className="text-muted-foreground">{i18n.api.description}</p>
                      </div>
                    </div>

                    <ApiManagement />
                  </div>
                </div>
              </TabsContent>

              {/* Attributes Tab */}
              {availableTabs.some(tab => tab.value === 'attributes') && (
                <TabsContent value="attributes" className="m-0 h-full">
                  <div className="p-8">
                    <div className="mb-6 flex items-center space-x-3">
                      <div className="flex size-10 items-center justify-center rounded-lg bg-cyan-100 dark:bg-cyan-900/30">
                        <svg
                          className="size-5 text-cyan-600 dark:text-cyan-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                          />
                        </svg>
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold text-foreground">
                          {i18n.attributes.title}
                        </h2>
                        <p className="text-muted-foreground">{i18n.attributes.description}</p>
                      </div>
                    </div>
                    <AttributesPage />
                  </div>
                </TabsContent>
              )}

              {/* Attribute Groups Tab */}
              {availableTabs.some(tab => tab.value === 'attribute-groups') && (
                <TabsContent value="attribute-groups" className="m-0 h-full">
                  <div className="p-8">
                    <div className="mb-6 flex items-center space-x-3">
                      <div className="flex size-10 items-center justify-center rounded-lg bg-teal-100 dark:bg-teal-900/30">
                        <svg
                          className="size-5 text-teal-600 dark:text-teal-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 11H5m14-4H3m16 8H1"
                          />
                        </svg>
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold text-foreground">
                          {i18n.attributeGroups.title}
                        </h2>
                        <p className="text-muted-foreground">{i18n.attributeGroups.description}</p>
                      </div>
                    </div>
                    <AttributeGroupsPage />
                  </div>
                </TabsContent>
              )}

              {/* Families Tab */}
              {availableTabs.some(tab => tab.value === 'families') && (
                <TabsContent value="families" className="m-0 h-full">
                  <div className="p-8">
                    <div className="mb-6 flex items-center space-x-3">
                      <div className="flex size-10 items-center justify-center rounded-lg bg-rose-100 dark:bg-rose-900/30">
                        <svg
                          className="size-5 text-rose-600 dark:text-rose-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 11H5m14-4H3m16 8H1"
                          />
                        </svg>
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold text-foreground">
                          {i18n.families.title}
                        </h2>
                        <p className="text-muted-foreground">{i18n.families.description}</p>
                      </div>
                    </div>
                    <FamilyListPage isEmbedded={true} />
                  </div>
                </TabsContent>
              )}
            </div>
          </div>
        </Tabs>
      </div>
    </div>
  );
};

export default SettingsPage;
